/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import React, { createRef, useState } from 'react';
import { Popup } from 'semantic-ui-react';

import { RiskDirectionType, RiskLikelihood } from 'models/RiskCategories';
import { RiskLikelihoodPopupTextMapping } from 'utils/modelHelpers';
import { isNonMobile } from 'utils/responsive';

import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { LikelihoodPerMonthExplanationText } from 'components/_static/StaticTexts';

import RiskLikelihoodIcon from './RiskLikelihoodIcon';

interface RiskLikelihoodPopupProps {
  riskLikelihood: RiskLikelihood;
  riskDirectionType: RiskDirectionType;
}
const RiskLikelihoodPopup: React.FC<RiskLikelihoodPopupProps> = ({
  riskLikelihood,
  riskDirectionType,
}) => {
  const contextRef = createRef<HTMLElement>();
  const [isOpen, setIsOpen] = useState(false);
  const isNonMobileDevice = isNonMobile(useBreakpointIndex());
  const likelihoodPerMonthText = LikelihoodPerMonthExplanationText(
    riskDirectionType === 'importation'
  );
  const riskLikelihoodText = RiskLikelihoodPopupTextMapping[riskLikelihood];
  const trigger = RiskLikelihoodIcon({ riskLikelihood, riskDirectionType });
  return (
    <span ref={contextRef}>
      <Popup
        wide
        open={isOpen}
        onOpen={e => {
          setIsOpen(true);
          e && e.stopPropagation && e.stopPropagation();
        }}
        onClose={e => {
          if (!isOpen) return;
          setIsOpen(false);
          e && e.nativeEvent && e.stopPropagation && e.stopPropagation();
        }}
        onClick={e => {
          setIsOpen(false);
          e && e.stopPropagation && e.stopPropagation();
        }}
        position="left center"
        trigger={trigger}
        hideOnScroll
        className="prob-icons"
        offset="-4px, 0"
        on={isNonMobileDevice ? ['hover', 'focus', 'click'] : ['click']}
        popperModifiers={{ preventOverflow: { boundariesElement: 'window' } }}
        context={contextRef}
      >
        {riskLikelihoodText.title && (
          <Typography variant="subtitle2" color="stone10" marginBottom="8px">
            {riskLikelihoodText.title}
          </Typography>
        )}
        <FlexGroup
          prefix={
            <RiskLikelihoodIcon
              riskLikelihood={riskLikelihood}
              riskDirectionType={riskDirectionType}
              dark
            />
          }
          alignItems="flex-start"
          gutter="2px"
        >
          <Typography variant="subtitle2" color="stone10">
            {riskLikelihoodText.message}
          </Typography>
        </FlexGroup>
        <Typography variant="caption" color="stone10" marginBottom="0">
          {likelihoodPerMonthText}
        </Typography>
      </Popup>
    </span>
  );
};

export default RiskLikelihoodPopup;
